import React, { useState } from 'react';
import styled from 'styled-components';

const API = process.env.REACT_APP_API_URL || '';

const TABS = ['Project', 'Products', 'Tracks', 'Contributors', 'Audio', 'Artwork'];

function PreviewStep({ sessionId, sessionData, editedData, setEditedData, onComplete }) {
  const [activeTab, setActiveTab] = useState('Project');
  const [saving, setSaving] = useState(false);

  const updateProject = (field, value) => {
    setEditedData(d => ({ ...d, project: { ...d.project, [field]: value } }));
  };

  const updateProduct = (idx, field, value) => {
    setEditedData(d => {
      const products = [...d.products];
      products[idx] = { ...products[idx], [field]: value };
      return { ...d, products };
    });
  };

  const updateTrack = (idx, field, value) => {
    setEditedData(d => {
      const tracks = [...d.tracks];
      tracks[idx] = { ...tracks[idx], [field]: value };
      return { ...d, tracks };
    });
  };

  const updateContributor = (idx, field, value) => {
    setEditedData(d => {
      const contributors = [...d.contributors];
      contributors[idx] = { ...contributors[idx], [field]: value };
      return { ...d, contributors };
    });
  };

  const handleProceed = () => {
    onComplete(editedData);
  };

  const counts = {
    Project: 1,
    Products: editedData?.products?.length || 0,
    Tracks: editedData?.tracks?.length || 0,
    Contributors: (editedData?.contributors?.length || 0) + (editedData?.otherContributors?.length || 0),
    Audio: sessionData?.audioFiles?.length || 0,
    Artwork: sessionData?.artworkFiles?.length || 0
  };

  return (
    <Container>
      <Sidebar>
        <SideHeader>
          <SideTitle>Review & Edit</SideTitle>
          <SideSub>Check extracted data before export</SideSub>
        </SideHeader>

        <TabList>
          {TABS.map(tab => (
            <Tab key={tab} active={activeTab === tab} onClick={() => setActiveTab(tab)}>
              <TabLabel>{tab}</TabLabel>
              <TabCount active={activeTab === tab}>{counts[tab]}</TabCount>
            </Tab>
          ))}
        </TabList>

        <SideFooter>
          <ProceedBtn onClick={handleProceed}>
            Proceed to Export →
          </ProceedBtn>
        </SideFooter>
      </Sidebar>

      <Content>
        {activeTab === 'Project' && (
          <ProjectPanel
            data={editedData?.project}
            update={updateProject}
            products={editedData?.products}
            updateProduct={updateProduct}
          />
        )}
        {activeTab === 'Products' && (
          <ProductsPanel products={editedData?.products} update={updateProduct} />
        )}
        {activeTab === 'Tracks' && (
          <TracksPanel tracks={editedData?.tracks} update={updateTrack} />
        )}
        {activeTab === 'Contributors' && (
          <ContributorsPanel
            contributors={editedData?.contributors}
            otherContributors={editedData?.otherContributors}
            update={updateContributor}
          />
        )}
        {activeTab === 'Audio' && (
          <AudioPanel
            audioFiles={sessionData?.audioFiles}
            tracks={editedData?.tracks}
            update={updateTrack}
          />
        )}
        {activeTab === 'Artwork' && (
          <ArtworkPanel
            artworkFiles={sessionData?.artworkFiles}
            sessionId={sessionId}
          />
        )}
      </Content>
    </Container>
  );
}

// ---- Sub-panels ----

function ProjectPanel({ data = {}, update, products = [], updateProduct }) {
  const handleReleaseDateChange = (v) => {
    update('releaseDate', v);
    // Cascade to all products
    products.forEach((_, i) => {
      updateProduct(i, 'releaseDate', v);
      updateProduct(i, 'saleStartDate', v);
    });
  };

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Project</PanelTitle>
        <PanelSub>Top-level project information</PanelSub>
      </PanelHeader>
      <FieldGrid>
        <Field label="Project Name" value={data.projectName} onChange={v => update('projectName', v)} span={2} />
        <Field label="Project Code" value={data.projectCode} onChange={v => update('projectCode', v)} mono />
        <Field label="Project Artist" value={data.projectArtist} onChange={v => update('projectArtist', v)} />
        <Field label="Release Date" value={data.releaseDate} onChange={handleReleaseDateChange} mono type="date" />
      </FieldGrid>
    </Panel>
  );
}

function ProductsPanel({ products = [], update }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Products</PanelTitle>
        <PanelSub>{products.length} product(s) — one per language variant</PanelSub>
      </PanelHeader>
      {products.map((p, i) => (
        <ProductCard key={i}>
          <ProductCardHeader>
            <ProductCode>{p.productCode}</ProductCode>
            <LangTag>{p.language || p.langCode}</LangTag>
            <FormatTag>{p.format}</FormatTag>
          </ProductCardHeader>

          <FixedRow>
            <FixedItem>
              <FixedLabel>Not Cleared for Sale</FixedLabel>
              <FixedValue>Russia, South Korea</FixedValue>
            </FixedItem>
            <FixedItem>
              <FixedLabel>Special Instructions</FixedLabel>
              <FixedValue>Please exclude Tencent &amp; YT CID</FixedValue>
            </FixedItem>
            <FixedItem>
              <FixedLabel>Release &amp; Sale Date</FixedLabel>
              <FixedValue mono>{p.releaseDate || '—'}</FixedValue>
            </FixedItem>
          </FixedRow>

          <FieldGrid>
            <Field label="Product Code" value={p.productCode} onChange={v => update(i, 'productCode', v)} mono />
            <Field label="Product Name" value={p.productName} onChange={v => update(i, 'productName', v)} />
            <Field label="Metadata Language" value={p.productMetadataLanguage} onChange={v => update(i, 'productMetadataLanguage', v)} />
            <Field label="Primary Artist" value={p.primaryArtist} onChange={v => update(i, 'primaryArtist', v)} />
            <Field label="Genre" value={p.genre} onChange={v => update(i, 'genre', v)} />
            <Field label="Subgenre" value={p.subgenre} onChange={v => update(i, 'subgenre', v)} />
            <Field label="Format" value={p.format} onChange={v => update(i, 'format', v)} />
            <Field label="Imprint" value={p.imprint} onChange={v => update(i, 'imprint', v)} />
            <Field label="(C) Line" value={p.cLine} onChange={v => update(i, 'cLine', v)} span={2} />
            <Field label="Artwork File" value={p.artworkFileName} onChange={v => update(i, 'artworkFileName', v)} mono span={2} />
          </FieldGrid>
        </ProductCard>
      ))}
      {products.length === 0 && <Empty>No products found in metadata</Empty>}
    </Panel>
  );
}

function TracksPanel({ tracks = [], update }) {
  const grouped = tracks.reduce((acc, t, i) => {
    const k = t.productCode || 'Unknown';
    if (!acc[k]) acc[k] = [];
    acc[k].push({ ...t, _idx: i });
    return acc;
  }, {});

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Tracks</PanelTitle>
        <PanelSub>{tracks.length} track(s) across {Object.keys(grouped).length} product(s)</PanelSub>
      </PanelHeader>
      {Object.entries(grouped).map(([code, groupTracks]) => (
        <TrackGroup key={code}>
          <TrackGroupHeader>{code}</TrackGroupHeader>
          {groupTracks.map((t) => (
            <TrackCard key={t._idx}>
              <TrackCardHeader>
                <TrackNum>#{t.trackNumber}</TrackNum>
                <TrackName>{t.trackName || '—'}</TrackName>
                {t.isrc && <ISRCBadge>{t.isrc}</ISRCBadge>}
              </TrackCardHeader>
              <FieldGrid>
                <Field label="ISRC" value={t.isrc} onChange={v => update(t._idx, 'isrc', v)} mono required />
                <Field label="Track Name" value={t.trackName} onChange={v => update(t._idx, 'trackName', v)} />
                <Field label="Track Number" value={t.trackNumber} onChange={v => update(t._idx, 'trackNumber', v)} mono />
                <Field label="Lyrics Language" value={t.lyricsLanguage} onChange={v => update(t._idx, 'lyricsLanguage', v)} />
                <Field label="Audio File" value={t.audioFileName} onChange={v => update(t._idx, 'audioFileName', v)} mono />
                <Field label="Primary Artist" value={t.primaryArtist} onChange={v => update(t._idx, 'primaryArtist', v)} />
                <Field label="Songwriter" value={t.songwriter} onChange={v => update(t._idx, 'songwriter', v)} />
                <Field label="Producer" value={t.producer} onChange={v => update(t._idx, 'producer', v)} />
                <Field label="Performer Legal Name" value={t.primaryPerformerLegalName} onChange={v => update(t._idx, 'primaryPerformerLegalName', v)} />
                <Field label="Performer Role" value={t.primaryPerformerRole} onChange={v => update(t._idx, 'primaryPerformerRole', v)} />
                <Field label="(P) Line" value={t.pLine} onChange={v => update(t._idx, 'pLine', v)} />
                <Field label="Country of Recording" value={t.countryOfRecording} onChange={v => update(t._idx, 'countryOfRecording', v)} />
                <Field label="Publishers" value={t.publishers} onChange={v => update(t._idx, 'publishers', v)} />
                <Field label="Explicit Content" value={t.explicitContent} onChange={v => update(t._idx, 'explicitContent', v)} />
                {t.trackLyrics && (
                  <Field label="Track Lyrics" value={t.trackLyrics} onChange={v => update(t._idx, 'trackLyrics', v)} span={2} multiline />
                )}
              </FieldGrid>
            </TrackCard>
          ))}
        </TrackGroup>
      ))}
      {tracks.length === 0 && <Empty>No tracks found in metadata</Empty>}
    </Panel>
  );
}

function ContributorsPanel({ contributors = [], otherContributors = [], update }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Contributors</PanelTitle>
        <PanelSub>{contributors.length} product contributor(s), {otherContributors.length} track contributor(s)</PanelSub>
      </PanelHeader>

      {contributors.length > 0 && (
        <>
          <SectionLabel>Product Contributors</SectionLabel>
          <Table>
            <thead>
              <tr>
                <Th>Product Code</Th>
                <Th>Role</Th>
                <Th>Name</Th>
              </tr>
            </thead>
            <tbody>
              {contributors.map((c, i) => (
                <tr key={i}>
                  <Td mono>{c.productCode}</Td>
                  <Td>{c.contributorRole}</Td>
                  <Td>{c.contributorName}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {otherContributors.length > 0 && (
        <>
          <SectionLabel style={{ marginTop: 24 }}>Track Contributors</SectionLabel>
          <Table>
            <thead>
              <tr>
                <Th>Product</Th>
                <Th>Track</Th>
                <Th>Type</Th>
                <Th>Role</Th>
                <Th>Name</Th>
              </tr>
            </thead>
            <tbody>
              {otherContributors.map((c, i) => (
                <tr key={i}>
                  <Td mono>{c.productCode}</Td>
                  <Td mono>#{c.trackNumber}</Td>
                  <Td>{c.contributorType}</Td>
                  <Td>{c.contributorRole}</Td>
                  <Td>{c.contributorName}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {contributors.length === 0 && otherContributors.length === 0 && (
        <Empty>No contributors found in metadata</Empty>
      )}
    </Panel>
  );
}

function AudioPanel({ audioFiles = [], tracks = [], update }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Audio Files</PanelTitle>
        <PanelSub>Files will be renamed to Orchard format on export</PanelSub>
      </PanelHeader>

      <Table>
        <thead>
          <tr>
            <Th>Original Filename</Th>
            <Th>→ Export Filename</Th>
            <Th>Track</Th>
          </tr>
        </thead>
        <tbody>
          {audioFiles.map((af, i) => {
            const matchedTrack = tracks.find(t => {
              const fn = af.filename.toLowerCase();
              const num = t.trackNumber;
              const lang = (t.langCode || '').toLowerCase();
              return fn.includes(`_${num}.`) || fn.includes(`${lang}_${num}`) || fn.includes(`${num}_${lang}`);
            });
            return (
              <tr key={i}>
                <Td mono style={{ color: 'var(--text-2)' }}>{af.filename}</Td>
                <Td mono style={{ color: 'var(--accent)' }}>
                  {matchedTrack?.audioFileName || af.filename}
                </Td>
                <Td style={{ color: 'var(--text-3)' }}>
                  {matchedTrack ? `${matchedTrack.productCode} #${matchedTrack.trackNumber}` : '—'}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {audioFiles.length === 0 && <Empty>No audio files found in zip</Empty>}
    </Panel>
  );
}

function ArtworkPanel({ artworkFiles = [], sessionId }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Artwork</PanelTitle>
        <PanelSub>Images converted to 3000×3000 JPEG</PanelSub>
      </PanelHeader>

      <ArtworkGrid>
        {artworkFiles.map((art, i) => (
          <ArtworkCard key={i}>
            {art.convertedFilename ? (
              <ArtworkImg
                src={`${API}/api/artwork/${sessionId}/${art.convertedFilename}`}
                alt={art.originalFilename}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <ArtworkPlaceholder>No preview</ArtworkPlaceholder>
            )}
            <ArtworkInfo>
              <ArtworkOrigName>{art.originalFilename}</ArtworkOrigName>
              <ArtworkMeta>
                {art.originalWidth}×{art.originalHeight} → 3000×3000
              </ArtworkMeta>
              {art.wasConverted ? (
                <ArtworkStatus ok>✓ Converted to JPEG</ArtworkStatus>
              ) : (
                <ArtworkStatus>⚠ Conversion failed</ArtworkStatus>
              )}
            </ArtworkInfo>
          </ArtworkCard>
        ))}
      </ArtworkGrid>

      {artworkFiles.length === 0 && <Empty>No artwork files found in zip</Empty>}
    </Panel>
  );
}

// ---- Reusable Field component ----
function Field({ label, value, onChange, mono, span, required, multiline, type }) {
  return (
    <FieldWrapper span={span}>
      <FieldLabel>
        {label}
        {required && !value && <RequiredDot title="Required field is empty" />}
      </FieldLabel>
      {multiline ? (
        <FieldTextarea
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          mono={mono}
          rows={4}
        />
      ) : (
        <FieldInput
          type={type || 'text'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          mono={mono}
          empty={!value}
        />
      )}
    </FieldWrapper>
  );
}

// ---- Styled components ----

const Container = styled.div`
  display: flex;
  height: calc(100vh - 67px);
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 210px;
  flex-shrink: 0;
  background: white;
  border-right: 2px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 1px 0 0 var(--border);
`;

const SideHeader = styled.div`
  padding: 20px 16px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--gradient-ocean);
`;

const SideTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  color: white;
  letter-spacing: -0.3px;
  margin-bottom: 2px;
`;

const SideSub = styled.p`
  font-size: 10px;
  color: rgba(255,255,255,0.65);
  line-height: 1.4;
  font-weight: 500;
`;

const TabList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const Tab = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  background: ${p => p.active ? 'var(--blue-dim)' : 'transparent'};
  border-left: 3px solid ${p => p.active ? 'var(--blue)' : 'transparent'};
  color: ${p => p.active ? 'var(--blue-dark)' : 'var(--text-2)'};
  font-size: 12px;
  font-weight: ${p => p.active ? '800' : '600'};
  transition: all 0.1s;
  &:hover {
    background: ${p => p.active ? 'var(--blue-dim)' : 'var(--bg-3)'};
    color: ${p => p.active ? 'var(--blue-dark)' : 'var(--text)'};
  }
`;

const TabLabel = styled.span``;

const TabCount = styled.span`
  font-size: 10px;
  background: ${p => p.active ? 'var(--blue)' : 'var(--bg-4)'};
  color: ${p => p.active ? 'white' : 'var(--text-3)'};
  padding: 2px 7px;
  border-radius: 10px;
  font-weight: 700;
`;

const SideFooter = styled.div`
  padding: 14px;
  border-top: 2px solid var(--border);
  background: var(--bg-3);
`;

const ProceedBtn = styled.button`
  width: 100%;
  padding: 11px;
  background: var(--gradient-ocean);
  color: white;
  font-family: var(--font-ui);
  font-weight: 700;
  font-size: 12px;
  border-radius: var(--radius);
  transition: opacity 0.15s, transform 0.15s;
  box-shadow: var(--shadow);
  &:hover { opacity: 0.9; transform: translateY(-1px); }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--bg);
`;

const Panel = styled.div`
  padding: 28px 32px;
  max-width: 900px;
`;

const PanelHeader = styled.div`
  margin-bottom: 24px;
`;

const PanelTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.5px;
  margin-bottom: 4px;
`;

const PanelSub = styled.p`
  font-size: 11px;
  color: var(--text-3);
  font-weight: 500;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const FieldWrapper = styled.div`
  grid-column: ${p => p.span === 2 ? '1 / -1' : 'auto'};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 10px;
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
`;

const RequiredDot = styled.span`
  width: 5px;
  height: 5px;
  background: var(--amber);
  border-radius: 50%;
  display: inline-block;
`;

const FieldInput = styled.input`
  background: ${p => p.empty ? 'var(--amber-dim)' : 'var(--bg-2)'};
  border: 1.5px solid ${p => p.empty ? 'var(--amber)' : 'var(--border)'};
  color: var(--text);
  padding: 7px 10px;
  border-radius: var(--radius);
  font-size: 12px;
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'var(--font-ui)'};
  font-weight: ${p => p.mono ? '400' : '600'};
  transition: border-color 0.1s, box-shadow 0.1s;
  &:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 3px var(--blue-dim);
  }
`;

const FieldTextarea = styled.textarea`
  background: white;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 7px 10px;
  border-radius: var(--radius);
  font-size: 11px;
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'var(--font-ui)'};
  resize: vertical;
  line-height: 1.5;
  &:focus { border-color: var(--blue); box-shadow: 0 0 0 3px var(--blue-dim); }
`;

const ProductCard = styled.div`
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: var(--shadow-sm);
`;

const ProductCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const ProductCode = styled.span`
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 500;
  color: var(--blue-dark);
  background: var(--blue-dim);
  padding: 3px 10px;
  border-radius: var(--radius);
`;

const FormatTag = styled.span`
  font-size: 10px;
  padding: 2px 8px;
  background: var(--accent-dim);
  border: 1.5px solid var(--accent-border);
  border-radius: var(--radius);
  color: var(--accent-text);
  font-weight: 600;
  font-family: var(--font-ui);
`;

const FixedRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const FixedItem = styled.div`
  background: var(--bg-3);
  border: 1.5px solid var(--border);
  border-radius: 10px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 160px;
`;

const FixedLabel = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-3);
  font-weight: 600;
`;

const FixedValue = styled.span`
  font-size: 11px;
  color: var(--text-2);
  font-weight: 600;
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'var(--font-ui)'};
`;

const LangTag = styled.span`
  font-size: 10px;
  padding: 2px 8px;
  background: var(--bg-4);
  border: 1.5px solid var(--border-2);
  border-radius: var(--radius);
  color: var(--text-2);
  font-weight: 700;
`;

const TrackGroup = styled.div`
  margin-bottom: 24px;
`;

const TrackGroupHeader = styled.div`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--blue-dark);
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
  font-weight: 500;
`;

const TrackCard = styled.div`
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 10px;
  box-shadow: var(--shadow-sm);
`;

const TrackCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const TrackNum = styled.span`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-2);
  background: var(--bg-4);
  padding: 2px 8px;
  border-radius: var(--radius);
  font-weight: 500;
`;

const TrackName = styled.span`
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  flex: 1;
`;

const ISRCBadge = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--blue-dark);
  background: var(--blue-dim);
  padding: 2px 8px;
  border-radius: var(--radius);
  letter-spacing: 0.5px;
`;

const SectionLabel = styled.h3`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-3);
  margin-bottom: 8px;
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: white;
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-2);
  border-bottom: 1px solid var(--border);
  background: var(--bg-3);
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  color: var(--text);
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'inherit'};
  font-weight: 500;
`;

const ArtworkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const ArtworkCard = styled.div`
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
`;

const ArtworkImg = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

const ArtworkPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: var(--text-3);
`;

const ArtworkInfo = styled.div`
  padding: 10px 12px;
`;

const ArtworkOrigName = styled.p`
  font-size: 11px;
  color: var(--text);
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
`;

const ArtworkMeta = styled.p`
  font-size: 10px;
  color: var(--text-3);
  font-family: var(--font-mono);
  margin-bottom: 4px;
`;

const ArtworkStatus = styled.p`
  font-size: 10px;
  color: ${p => p.ok ? 'var(--green)' : 'var(--amber)'};
  font-weight: 700;
`;

const Empty = styled.div`
  padding: 32px;
  text-align: center;
  color: var(--text-3);
  font-size: 13px;
  font-weight: 600;
  background: white;
  border: 2px dashed var(--border);
  border-radius: var(--radius-lg);
`;

export default PreviewStep;